/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { jsx } from '@emotion/react';

import * as React from 'react';

import type { SectionName } from '@coursera/event-pulse-types';
import { useVisibilityTracker } from '@coursera/event-pulse/react';

import TrackedDiv from 'bundles/page/components/TrackedDiv';
import type {
  PromoTemplateArticlesCollectionFragment,
  PromoTemplateCannedCollectionFragment,
  PromoTemplateCitationFragment,
  PromoTemplateCollectionsFragment,
  PromoTemplateFaqsFragment,
  PromoTemplateHeroFragment,
  PromoTemplateInformationFragment,
  PromoTemplatePartnerListFragment,
  PromoTemplatePromoBarFragment,
  PromoTemplateTestimonialCarouselFragment,
  PromoTemplateValuePropsFragment,
} from 'bundles/promo-template/__generated__/contentfulTypes';
import { eventingTransform } from 'bundles/promo-template/utils/eventingTransform';

export type BlockLevelModule =
  | PromoTemplateCollectionsFragment
  | PromoTemplateHeroFragment
  | PromoTemplatePartnerListFragment
  | PromoTemplateTestimonialCarouselFragment
  | PromoTemplateValuePropsFragment
  | PromoTemplateCannedCollectionFragment
  | PromoTemplateInformationFragment
  | PromoTemplateArticlesCollectionFragment
  | PromoTemplateFaqsFragment
  | PromoTemplateCitationFragment
  | PromoTemplatePromoBarFragment;

type Props = {
  content: BlockLevelModule;
};

// section name is used for tracking view_page_section event, thus new section name should be added to the enum list in eventing-schemas first
// https://github.com/webedx-spark/eventing-schemas/blob/d80a324cc2469f486252dce28595105f7129696a/schemas/eventingv3/entities/ui/page_section.json
const getSectionName = (typename: string) => {
  switch (typename) {
    case 'PromoTemplateHero':
      return 'hero_banner';
    case 'PromoTemplateValueProps':
      return 'value_proposition';
    case 'PromoTemplateCollections':
      return 'promo_template_collections';
    case 'PromoTemplatePartnerList':
      return 'partner_logo_list';
    case 'PromoTemplateTestimonialCarousel':
      return 'learner_testimonials';
    case 'PromoTemplateCannedCollection':
      return 'canned_collection';
    case 'PromoTemplateInformation':
      return 'information';
    case 'PromoTemplateArticlesCollection':
      return 'articles_collection';
    case 'PromoTemplateFaqs':
      return 'faqs';
    case 'PromoTemplateCitation':
      return 'citation';
    case 'PromoTemplatePromoBar':
      return 'promo_bar';
    default:
      return '';
  }
};

export const getModuleName = (typename: string) => {
  if (!typename) {
    return '';
  }
  // convert typename to tracking name that fits the naming convension
  const moduleName = typename.replace(/^PromoTemplate/, '');
  return (moduleName[0] + moduleName.slice(1).replace(/([A-Z])/g, '_$1')).toLowerCase();
};

const Module: React.LegacyFunctionComponentWithChildren<Props> = ({ children, content }) => {
  const moduleName = getModuleName(content.__typename ?? '');
  const trackingName = eventingTransform(moduleName);

  const sectionName = getSectionName(content.__typename ?? '');

  const visibilityTrackingRef: React.MutableRefObject<HTMLDivElement | null> = useVisibilityTracker(
    'view_page_section',
    {
      pageSection: {
        sectionName: getSectionName(content.__typename ?? '') as SectionName,
      },
    }
  );

  return (
    <TrackedDiv trackingName={trackingName} withVisibilityTracking trackClicks={false}>
      <div {...(sectionName ? { ref: visibilityTrackingRef } : {})}>{children}</div>
    </TrackedDiv>
  );
};

export default Module;
